import { useMemo } from "react";
import { useStore } from "ra-core";

import type { DealStage, LabeledValue, NoteStatus } from "../types";
import { defaultConfiguration } from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export const CONFIGURATION_VERSION_KEY = "app.configuration.version";

/** Identifier of the running build (falls back to "dev" outside Vite). */
export const APP_VERSION =
  typeof __APP_VERSION__ === "undefined" ? "dev" : __APP_VERSION__;

/**
 * Seeds the stored configuration from the bundled defaults when it is absent
 * or was written by a different build. Server-saved settings still win: they
 * overwrite the seed once the configuration loader fetches them.
 */
export const seedConfiguration = (
  store: {
    getItem: (key: string) => unknown;
    setItem: (key: string, value: unknown) => void;
  },
  defaults: ConfigurationContextValue,
  version: string = APP_VERSION,
) => {
  const isCurrent = store.getItem(CONFIGURATION_VERSION_KEY) === version;
  if (isCurrent && store.getItem(CONFIGURATION_STORE_KEY)) return;
  store.setItem(CONFIGURATION_STORE_KEY, defaults);
  store.setItem(CONFIGURATION_VERSION_KEY, version);
};

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealSources: LabeledValue[];
  dealStages: DealStage[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
}

/**
 * Adds built-in default stages (matched by value) that are absent from a saved
 * stage list. Saved stages keep their labels and order; each missing default is
 * inserted right after the nearest preceding default present in the list, at
 * the start if none precedes it, or appended if the saved list holds no default.
 * Returns the saved list itself when nothing is missing. Inputs are not mutated.
 */
export const mergeMissingDefaultStages = (
  saved: DealStage[],
  defaults: DealStage[],
): DealStage[] => {
  const savedValues = new Set(saved.map((stage) => stage.value));
  const missing = defaults.filter((stage) => !savedValues.has(stage.value));
  if (missing.length === 0) return saved;
  if (missing.length === defaults.length) return [...saved, ...missing];

  const result = [...saved];
  defaults.forEach((stage, index) => {
    if (savedValues.has(stage.value)) return;
    const precedingValues = new Set(
      defaults.slice(0, index).map((previous) => previous.value),
    );
    let insertAt = 0;
    result.forEach((item, position) => {
      if (precedingValues.has(item.value)) insertAt = position + 1;
    });
    result.splice(insertAt, 0, stage);
  });
  return result;
};

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(() => {
    const merged = { ...defaultConfiguration, ...config };
    return {
      ...merged,
      dealStages: mergeMissingDefaultStages(
        merged.dealStages,
        defaultConfiguration.dealStages,
      ),
    };
  }, [config]);
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
